import React, { useState, useEffect, useCallback } from 'react';
import { examenService, unwrapList } from '../services/api';

const ExamenesSection = ({ temas, onRefresh }) => {
  const [examenes, setExamenes] = useState({});
  const [loading, setLoading] = useState({});
  const [editingExamen, setEditingExamen] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    numero_preguntas: '',
    puntos_por_pregunta: 1,
    tiempo_limite: '',
    fecha_inicio: '',
    fecha_fin: '',
    porcentaje_aprobacion: 70,
    activo: true,
  });

  const loadExamenes = useCallback(async () => {
    const nuevosExamenes = {};
    const nuevosLoading = {};

    for (const tema of temas) {
      nuevosLoading[tema.id] = true;
      try {
        const response = await examenService.getAll(tema.id);
        const examenesTema = unwrapList(response);
        nuevosExamenes[tema.id] = examenesTema.length > 0 ? examenesTema[0] : null;
      } catch (err) {
        console.error(`Error al cargar examen del tema ${tema.id}:`, err);
        nuevosExamenes[tema.id] = null;
      } finally {
        nuevosLoading[tema.id] = false;
      }
    }

    setExamenes(nuevosExamenes);
    setLoading(nuevosLoading);
  }, [temas]);

  useEffect(() => {
    loadExamenes();
  }, [loadExamenes]);

  const handleEdit = (tema, examen) => {
    setEditingExamen({ tema, examen });
    if (examen) {
      setFormData({
        titulo: examen.titulo || '',
        descripcion: examen.descripcion || '',
        numero_preguntas: examen.numero_preguntas ?? '',
        puntos_por_pregunta: examen.puntos_por_pregunta || 1,
        tiempo_limite: examen.tiempo_limite || '',
        fecha_inicio: examen.fecha_inicio ? examen.fecha_inicio.substring(0, 16) : '',
        fecha_fin: examen.fecha_fin ? examen.fecha_fin.substring(0, 16) : '',
        porcentaje_aprobacion: examen.porcentaje_aprobacion ?? 70,
        activo: examen.activo !== undefined ? examen.activo : true,
      });
    } else {
      setFormData({
        titulo: '',
        descripcion: '',
        numero_preguntas: '',
        puntos_por_pregunta: 1,
        tiempo_limite: '',
        fecha_inicio: '',
        fecha_fin: '',
        porcentaje_aprobacion: 70,
        activo: true,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingExamen) return;

    try {
      const numeroPreguntas = parseInt(formData.numero_preguntas, 10);
      if (Number.isNaN(numeroPreguntas)) {
        alert('Debes indicar cuántas preguntas se seleccionarán del banco.');
        return;
      }
      const dataToSend = {
        ...formData,
        tema: editingExamen.tema.id,
        numero_preguntas: numeroPreguntas,
        puntos_por_pregunta: parseInt(formData.puntos_por_pregunta),
        porcentaje_aprobacion: parseInt(formData.porcentaje_aprobacion, 10) || 70,
        tiempo_limite: formData.tiempo_limite ? parseInt(formData.tiempo_limite) : null,
      };

      if (editingExamen.examen) {
        await examenService.update(editingExamen.examen.id, dataToSend);
      } else {
        await examenService.create(dataToSend);
      }
      resetForm();
      loadExamenes();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error completo:', err.response?.data);
      alert('Error al guardar examen: ' + (err.response?.data?.detail || err.response?.data?.tema || err.message));
    }
  };

  const resetForm = () => {
    setEditingExamen(null);
    setFormData({
      titulo: '',
      descripcion: '',
      numero_preguntas: '',
      puntos_por_pregunta: 1,
      tiempo_limite: '',
      fecha_inicio: '',
      fecha_fin: '',
      porcentaje_aprobacion: 70,
      activo: true,
    });
  };

  return (
    <div className="examenes-section">
      <div className="section-header">
        <div>
          <h2>Exámenes</h2>
          <p style={{margin: '4px 0 0 0', color: '#666', fontSize: '0.95rem'}}>
            Cada tema tiene un examen.
          </p>
        </div>
      </div>

      {editingExamen && (
        <form onSubmit={handleSubmit} className="new-form" style={{marginBottom: '30px'}}>
          <div className="section-header" style={{marginBottom: '20px'}}>
            <h3>
              {editingExamen.examen ? 'Editar Examen' : 'Crear Examen'} - {editingExamen.tema.titulo}
            </h3>
            <button type="button" onClick={resetForm} className="btn-secondary">
              Cancelar
            </button>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Título (opcional)</label>
              <input
                type="text"
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Descripción (opcional)</label>
              <input
                type="text"
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Número de Preguntas *</label>
              <input
                type="number"
                value={formData.numero_preguntas}
                onChange={(e) => setFormData({ ...formData, numero_preguntas: e.target.value })}
                min="1"
                required
              />
              <small>Indica cuántas se seleccionarán del banco</small>
            </div>
            <div className="form-group">
              <label>Puntos por Pregunta *</label>
              <input
                type="number"
                value={formData.puntos_por_pregunta}
                onChange={(e) => setFormData({ ...formData, puntos_por_pregunta: parseInt(e.target.value) || 1 })}
                min="1"
                required
              />
            </div>
            <div className="form-group">
              <label>Tiempo Límite (minutos)</label>
              <input
                type="number"
                value={formData.tiempo_limite}
                onChange={(e) => setFormData({ ...formData, tiempo_limite: e.target.value })}
                min="1"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Fecha y Hora de Inicio *</label>
              <input
                type="datetime-local"
                value={formData.fecha_inicio}
                onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Fecha y Hora de Fin *</label>
              <input
                type="datetime-local"
                value={formData.fecha_fin}
                onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Porcentaje para aprobar (%) *</label>
              <input
                type="number"
                value={formData.porcentaje_aprobacion}
                onChange={(e) => setFormData({ ...formData, porcentaje_aprobacion: e.target.value })}
                min="0"
                max="100"
                required
              />
              <small>Ej: 70 significa que se aprueba con 70% o más</small>
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.activo}
                onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
              />
              <span>Examen Activo</span>
            </label>
          </div>

          <button type="submit" className="btn-primary">
            {editingExamen.examen ? 'Actualizar Examen' : 'Crear Examen'}
          </button>
        </form>
      )}

      <div className="examenes-list">
        {temas.map((tema) => {
          const examen = examenes[tema.id];
          const isLoading = loading[tema.id];

          return (
            <div key={tema.id} className="examen-item-card">
              <div className="examen-item-header">
                <div className="examen-item-title">
                  <h3>{tema.titulo}</h3>
                </div>
                <button
                  onClick={() => handleEdit(tema, examen)}
                  className="btn-examen-action"
                >
                  {examen ? 'Editar Examen' : 'Crear Examen'}
                </button>
              </div>
              
              <div className="examen-item-content">
                {isLoading ? (
                  <div className="examen-loading">
                    <p>Cargando información del examen...</p>
                  </div>
                ) : examen ? (
                  <div className="examen-info-grid">
                    <div className="examen-info-item">
                      <span className="examen-info-label">Preguntas:</span>
                      <span className="examen-info-value">{examen.numero_preguntas} aleatorias</span>
                    </div>
                    <div className="examen-info-item">
                      <span className="examen-info-label">Puntos por pregunta:</span>
                      <span className="examen-info-value">{examen.puntos_por_pregunta}</span>
                    </div>
                    <div className="examen-info-item">
                      <span className="examen-info-label">Puntaje total:</span>
                      <span className="examen-info-value">{examen.puntaje_total || (examen.numero_preguntas * examen.puntos_por_pregunta)}</span>
                    </div>
                    {examen.tiempo_limite && (
                      <div className="examen-info-item">
                        <span className="examen-info-label">Tiempo límite:</span>
                        <span className="examen-info-value">{examen.tiempo_limite} minutos</span>
                      </div>
                    )}
                    {examen.fecha_inicio && (
                      <div className="examen-info-item">
                        <span className="examen-info-label">Disponible desde:</span>
                        <span className="examen-info-value">{new Date(examen.fecha_inicio).toLocaleString('es-ES')}</span>
                      </div>
                    )}
                    {examen.fecha_fin && (
                      <div className="examen-info-item">
                        <span className="examen-info-label">Disponible hasta:</span>
                        <span className="examen-info-value">{new Date(examen.fecha_fin).toLocaleString('es-ES')}</span>
                      </div>
                    )}
                    <div className="examen-info-item">
                      <span className="examen-info-label">Aprobación:</span>
                      <span className="examen-info-value">≥{examen.porcentaje_aprobacion ?? 70}%</span>
                    </div>
                    <div className="examen-info-item">
                      <span className="examen-info-label">Estado:</span>
                      <span className={`badge ${examen.activo ? 'active' : 'inactive'}`}>
                        {examen.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="examen-empty-state">
                    <p>No hay examen creado para este tema</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {temas.length === 0 && (
          <div className="empty-state">
            <p>Crea temas primero para crear exámenes.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExamenesSection;



